import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { API_BASE_URL } from '@/lib/api';

export function QuickBooksCallback() {
  const { businessId, nav } = useBusinessNav();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    const code = searchParams.get('code');
    const realmId = searchParams.get('realmId');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(`QuickBooks authorization failed: ${errorParam}`);
      setStatus('error');
      return;
    }

    if (!code || !realmId || !state) {
      setError('Missing required authorization parameters');
      setStatus('error');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('Please log in and try again');
        setStatus('error');
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/accounting/quickbooks/callback/?code=${encodeURIComponent(code)}&realmId=${encodeURIComponent(realmId)}&state=${encodeURIComponent(state)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to connect to QuickBooks');
        setStatus('error');
        return;
      }

      setCompanyName(data.company_name || '');
      setStatus('success');

      // Redirect after a short delay
      // Extract business ID from state (format: "bus_xxx:random_token") for top-level route
      const stateBusinessId = businessId || state?.split(':')[0];
      setTimeout(() => {
        if (stateBusinessId) {
          navigate(`/${stateBusinessId}/settings/accounting?connected=quickbooks`);
        } else {
          navigate('/');
        }
      }, 2000);
    } catch (err) {
      console.error('QuickBooks callback error:', err);
      setError('Failed to connect to QuickBooks. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'loading' && (
            <>
              <div className="mx-auto mb-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
              <CardTitle>Connecting to QuickBooks</CardTitle>
              <CardDescription>
                Please wait while we complete the connection...
              </CardDescription>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto mb-4">
                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <CardTitle>Connected!</CardTitle>
              <CardDescription>
                {companyName ? (
                  <>Successfully connected to <strong>{companyName}</strong></>
                ) : (
                  'Successfully connected to QuickBooks'
                )}
              </CardDescription>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto mb-4">
                <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mx-auto">
                  <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <CardTitle>Connection Failed</CardTitle>
              <CardDescription className="text-red-600 dark:text-red-400">
                {error}
              </CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent className="text-center">
          {status === 'success' && (
            <p className="text-sm text-muted-foreground mb-4">
              Redirecting to settings...
            </p>
          )}

          {status === 'error' && (
            <div className="space-y-2">
              <Button onClick={() => {
                const stateBusinessId = businessId || searchParams.get('state')?.split(':')[0];
                if (stateBusinessId) {
                  navigate(`/${stateBusinessId}/settings/accounting`);
                } else {
                  navigate('/');
                }
              }} className="w-full">
                Back to Settings
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="w-full"
              >
                Try Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
